<script setup lang="ts">
import { IconBagFill } from "@packages/icon-library";
import { UiBadge, UiButton, UiCounter } from "@packages/component-library";
import { IPlaceProductCard, usePlaceMenuStore } from "@/modules/place-module";
import { computed, onBeforeMount, PropType, ref } from "vue";
import { useRoute } from "vue-router";
import { useCartStore } from "@/modules/cart-module";
import { useSystemStore } from "@/store/system.store";
import { useI18n } from "vue-i18n";
import { useUserStore } from "@/modules/user-module";

const props = defineProps({
  product: {
    type: Object as PropType<IPlaceProductCard>,
    required: true,
  },

  actions: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const route = useRoute();
const systemStore = useSystemStore();
const userStore = useUserStore();
const placeMenuStore = usePlaceMenuStore();
const orderAllowed = computed(() => placeMenuStore.orderAllowed);
const cartStore = useCartStore();
const selectSlide = ref(0);
const count = ref(0);
const user = computed(() => userStore.user);

const updateProductInCart = (change: "add" | "remove") => {
  if (!user.value) {
    return;
  }

  if (change === "add") {
    count.value++;
  }
  if (change === "remove") {
    count.value--;
  }

  cartStore.updateCartProduct({
    product_id: props.product?.id,
    count: count.value,
  });
};

const updateProductCountInCart = (newCount: number) => {
  if (!user.value) {
    return;
  }

  if (props.product?.price === 0) {
    systemStore.showNotification({
      type: "error",
      text: t("place.menu.addZeroProductError"),
    });
    return;
  }

  count.value = newCount;

  cartStore.updateCartProduct({
    product_id: props.product?.id,
    count: count.value,
  });
};

const routeQueries = computed(() => {
  return { ...route.query, product: props.product.id };
});

onBeforeMount(() => {
  count.value = props.product?.count_in_cart;
});
</script>

<template>
  <div class="product-card">
    <RouterLink :to="{ query: routeQueries }">
      <div class="product-card__slider slider">
        <div
          class="slider__image"
          :style="{
            backgroundImage: product.images.length
              ? `url(${product.images[selectSlide]})`
              : '',
          }"
        >
          <template v-if="product.images.length">
            <span
              v-for="(item, index) in product.images"
              :key="index"
              @mouseover="selectSlide = index"
            />
          </template>

          <img v-else src="@/assets/images/no-photo.svg" alt="no-photo" />
        </div>

        <div class="slider__controls">
          <template v-if="product.images.length > 1">
            <div
              v-for="(item, index) in product.images"
              :key="index"
              class="slider__control"
            >
              <span :class="{ active: index === selectSlide }" />
            </div>
          </template>
        </div>
      </div>
      <p>
        {{ product.price }} {{ product.currency }}
        <template v-if="product.discount_percent">
          <del>{{ product.old_price }}</del>
          <UiBadge
            :label="`-${product.discount_percent}%`"
            color="red"
            type="soft"
            size="s"
          />
        </template>
      </p>
      <h6>{{ product.title }}</h6>
    </RouterLink>

    <div v-if="orderAllowed" class="product-card__actions">
      <UiButton
        v-if="count === 0"
        size="m"
        :label="t('place.menu.addToCart')"
        color="primary"
        :disabled="!user"
        data-test-id="placeProduct-button-addCart"
        @click="updateProductCountInCart(1)"
      >
        <IconBagFill />
      </UiButton>

      <UiCounter
        v-else
        v-model="count"
        :max="999"
        size="m"
        @increment="updateProductInCart('add')"
        @decrement="updateProductInCart('remove')"
        @update="updateProductCountInCart"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.product-card {
  width: 100%;
  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: calc($base-space * 3);
  flex-shrink: 0;

  > a {
    width: 100%;
    > h6 {
      margin: calc($base-space * 2) 0 0;
      @include body-m();
      color: var(--color-semantic-content-normal-primary);
      @include max-stroke(2);
    }

    > p {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: calc($base-space * 2);
      margin: calc($base-space * 3) 0 0;
      @include body-bold-l();
      color: var(--color-semantic-content-normal-primary);

      > del {
        @include body-s();
        color: var(--color-semantic-content-normal-tertiary);
      }
    }
  }

  &__slider {
    min-height: 200px;

    @media (max-width: 1023px) {
      min-height: 170px;
    }
  }

  &__actions {
    width: 100%;
    margin: auto 0 0;
    > button {
      width: 100%;
    }
  }
}

.slider {
  &__image {
    aspect-ratio: 1/1;
    @include centerBackgroundImageCover();
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background-color: var(--color-semantic-background-normal-secondary);

    > p {
      @include body-bold-l();
      color: var(--color-semantic-content-normal-tertiary);
      text-align: center;
      max-width: 120px;
    }

    > span {
      height: 100%;
      width: 100%;
    }
  }

  &__controls {
    margin: calc($base-space * 2) 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 8px;
  }

  &__control {
    padding: calc($base-space / 2);
    display: flex;
    align-items: center;
    justify-content: center;

    > span {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background-color: var(--color-semantic-background-normal-tertiary);
      transition: all 0.1s ease-out;

      &.active {
        width: 12px;
        border-radius: 4px;
        background-color: var(--color-semantic-background-normal-contrast);
      }
    }
  }
}
</style>
